import { motion } from "framer-motion";
import { ShoppingBag, ArrowUpRight } from "lucide-react";
import { EASE } from "../SectionHead";
import { railTrack, railItem, SwipeHint } from "../Rail";
import { BlurImg } from "../BlurImg";

const Product = ({ p, i, fallback }) => {
  const href = p.sold_out ? "" : p.url || fallback;
  const Tag = href ? "a" : "div";
  return (
    <motion.div className={railItem} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 0.7, delay: (i % 4) * 0.07, ease: EASE }}>
      <Tag {...(href ? { href, target: "_blank", rel: "noreferrer" } : {})} data-testid={`merch-item-${p.id}`}
        className={`group block clip-corner p-px transition-colors duration-300 ${p.sold_out ? "bg-white/10" : "bg-[#F5A524]/25 hover:bg-[#F5A524]"}`}>
        <article className="clip-corner bg-[#12131A] overflow-hidden">
          <div className={`relative aspect-square ${p.sold_out ? "grayscale opacity-60" : ""}`}>
            {p.image_url ? <BlurImg src={p.image_url} w={480} alt={p.name} imgClassName="group-hover:scale-105" /> : <span className="absolute inset-0 grid place-items-center text-[#F5A524]/40"><ShoppingBag size={40} /></span>}
            {p.tag && <span className="absolute top-3 left-3 bg-black/80 border border-[#F5A524]/40 px-2 py-1 font-mono text-[10px] tracking-[0.25em] text-[#F5A524] uppercase">{p.tag}</span>}
            {p.sold_out && <span className="absolute inset-0 grid place-items-center"><span className="-rotate-12 border-2 border-[#FF2A55] text-[#FF2A55] font-display font-black text-3xl px-3 bg-black/60">SOLD OUT</span></span>}
          </div>
          <div className="p-4 flex items-end justify-between gap-3">
            <div className="min-w-0">
              <h4 className="font-display font-bold uppercase text-xl leading-tight truncate group-hover:text-[#F5A524] transition-colors">{p.name || "Untitled"}</h4>
              {p.price && <p data-testid={`merch-item-price-${p.id}`} className="mt-1 font-mono text-sm text-zinc-300">{p.price}</p>}
            </div>
            {href && <span className="shrink-0 flex items-center gap-1 font-mono text-[11px] font-bold tracking-[0.2em] text-[#F5A524]">BUY <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>}
          </div>
        </article>
      </Tag>
    </motion.div>
  );
};

export const MerchBlock = ({ site }) => {
  const items = site.merch_items ?? [];
  return (
    <div data-testid="merch-block">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-8">
        <div>
          <p className="flex items-center gap-2 font-mono text-xs tracking-[0.3em] text-[#F5A524]"><ShoppingBag size={16} /> // THE STORE</p>
          <h3 className="mt-3 font-display font-black uppercase text-4xl sm:text-5xl leading-[0.9]">Prints, pins<br />& paper</h3>
          {site.merch_blurb && <p className="mt-4 text-base text-zinc-300 leading-relaxed max-w-md">{site.merch_blurb}</p>}
        </div>
        {site.shop_url && (
          <a href={site.shop_url} target="_blank" rel="noreferrer" data-testid="merch-shop-link"
            className="self-start md:self-auto inline-flex items-center gap-3 clip-tab border border-[#F5A524]/50 text-[#F5A524] px-6 py-3.5 font-mono text-xs font-bold tracking-[0.25em] hover:bg-[#F5A524] hover:text-black transition-colors">
            VISIT THE FULL SHOP <ArrowUpRight size={15} />
          </a>
        )}
      </div>
      {items.length > 0 && (
        <>
          <SwipeHint count={items.length} testid="merch-swipe-hint" />
          <div className={`${railTrack} md:grid md:grid-cols-3 xl:grid-cols-4 md:gap-5`}>
            {items.map((p, i) => <Product key={p.id} p={p} i={i} fallback={site.shop_url} />)}
          </div>
        </>
      )}
    </div>
  );
};
